import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Divider, TextField, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSnackbar } from "notistack";
import { error as logError } from "logger";
import {
  RightDrawer,
  MultipleSelect,
  SingleSelect,
  useLazyLoadingQueryOptionsHook,
  SelectOptionsHook,
} from "@coherehealth/common";
import {
  useGetProvidersOrFacilities,
  ProviderFacilityCatalogue,
  UseGetProvidersOrFacilitiesProps,
} from "@coherehealth/core-platform-api";
import config from "api/config";
import {
  User,
  useGetUser,
  useUpdateUser,
  useCreateUser,
  useGetSkills,
  useGetAllQueues,
  useGetReferenceStates,
  useGetReferenceRoles,
  useGetReferenceOpsGroups,
  useGetReferenceAuthCategories,
} from "@coherehealth/qm-api";
import EditableChipList from "./EditableChipList";
import {
  US_STATE_OPTIONS,
  makeStaticOptionsHook,
  makeStaticObjectOptionsHook,
  ActiveQueueOption,
} from "./editUserFormOptions";

// Outlined-chip accents per Figma (node 7988:41851 skillsets, 7988:42213 queues).
// Skillsets use the primary accent; queues use the "Part approved" magenta.
const SKILL_CHIP_ACCENT = "#039EC3";
const SKILL_CHIP_BACKGROUND = "#E0F4FC";
const SKILL_CHIP_FONT_COLOR = "#027691";
const QUEUE_CHIP_ACCENT = "#B028B0";
const QUEUE_CHIP_BACKGROUND = "#FAF0FA";
const QUEUE_CHIP_FONT_COLOR = "#A002A0";
const AUTH_CAT_CHIP_ACCENT = "#039EC3";
const AUTH_CAT_CHIP_BACKGROUND = "#E0F4FC";
const AUTH_CAT_CHIP_FONT_COLOR = "#027691";
// Figma font tokens (Font/Primary, Font/Secondary, Font/Tertiary) and input border tokens.
const FONT_PRIMARY = "#212936";
const FONT_SECONDARY = "#48505c";
const FONT_TERTIARY = "#657286";
const DIVIDER_COLOR = "#dadfe8"; // Borders/Primary
const PAGE_BACKGROUND = "#f8fbfc"; // Backgrounds/Page
const INPUT_BORDER = "#e5e5e5";
const INPUT_BORDER_ACTIVE = "#039ec3";

// Label mirrors authtool's FacilitySelectManual: "Facility Name / NPI - {npi}".
// Falls back to the raw NPI (e.g. for stub objects created from persisted strings).
function facilityNpiLabel(f: ProviderFacilityCatalogue): string {
  const name = f.locations?.[0]?.name;
  const npi = f.npi?.trim();
  if (name && npi) {
    return `${name} / NPI - ${npi}`;
  }
  return npi ?? f.id ?? "";
}

// Returns full catalogue objects so the dropdown can search and display by facility
// name in addition to NPI.  Equality is compared by NPI in the MultipleSelect below.
const useFacilityNpiOptions: SelectOptionsHook<ProviderFacilityCatalogue> = (params) => {
  const result = useLazyLoadingQueryOptionsHook<ProviderFacilityCatalogue, unknown>({
    useGetHook: (args: UseGetProvidersOrFacilitiesProps) =>
      useGetProvidersOrFacilities({ ...args, base: config.SERVICE_API_URL }),
    additionalQueryParams: { recordType: "FACILITY" },
    ...params,
  });
  // Pass options through unchanged; the API already applies the user's query server-side.
  return { ...result, filterOptions: (options) => options };
};

export interface EditUserDrawerProps {
  open: boolean;
  /** Id of the user being edited; the drawer loads fresh data via useGetUser. */
  userId: string | undefined;
  onClose: () => void;
  onSaved?: (user: User) => void;
  /**
   * "edit" (default) loads + PATCHes an existing user. "add" is reserved for the
   * Add-user flow (RQM-429, POST /user/) — the shared shell is built here but
   * the create path is wired in that story.
   */
  mode?: "edit" | "add";
}

function arraysEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (email: string) => EMAIL_REGEX.test(email.trim());

// Tooltip styling per Figma spec:
// width 288px, dark bg #2b3648, white text, 8px radius, level-2 shadow.
// Arrow is styled to match the bg and acts as the visual connector to the icon.
const tooltipComponentsProps = {
  tooltip: {
    sx: {
      width: 288,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      bgcolor: "#2b3648",
      color: "white",
      borderRadius: "8px",
      fontSize: 16,
      padding: "16px 24px",
      boxShadow: "0px 2px 4px 0px rgba(0, 69, 85, 0.16), 0px 2px 6px 2px rgba(0, 69, 85, 0.08)",
    },
  },
  arrow: {
    sx: { color: "#2b3648" },
  },
} as const;

// Add-user free-text fields (Figma 8619:52751-52753): 40px, 4px radius, white fill, 1px #e5e5e5 border
// at rest/filled; hover and focus switch the border to the primary teal (#039ec3) with no extra
// thickness. Placeholder uses Font/Tertiary.
const accountDetailFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "4px",
    fontSize: 16,
    color: FONT_PRIMARY,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: INPUT_BORDER, borderWidth: 1 },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: INPUT_BORDER_ACTIVE },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: INPUT_BORDER_ACTIVE, borderWidth: 1 },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#c62828" },
  },
  "& .MuiOutlinedInput-input": {
    "&::placeholder": { color: FONT_TERTIARY, opacity: 1 },
  },
} as const;

function sortedAuthCategoryValues(priorities: { [key: string]: number } | undefined): string[] {
  if (!priorities) {
    return [];
  }
  return Object.entries(priorities)
    .sort(([, a], [, b]) => a - b)
    .map(([category]) => category);
}

// Two-column section row: left label/subtitle, right content — matches the drawer design.
function Section({
  title,
  subtitle,
  children,
  testId,
  titleAdornment,
  alignItems = "flex-start",
  noPaddingBottom = false,
  noDivider = false,
}: {
  title: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  testId?: string;
  titleAdornment?: React.ReactNode;
  alignItems?: "flex-start" | "center";
  noPaddingBottom?: boolean;
  noDivider?: boolean;
}) {
  return (
    <>
      <Box sx={{ display: "flex", gap: 3, pt: 3, pb: noPaddingBottom ? 0 : 3, alignItems }} data-testid={testId}>
        <Box sx={{ width: 200, flexShrink: 0 }}>
          {/* Figma "Body Strong" 16/600 Font/Primary, pt:2px aligns baseline with input field */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pt: "2px" }}>
            <Typography sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.2, color: FONT_PRIMARY }}>
              {title}
            </Typography>
            {titleAdornment}
          </Box>
          {subtitle && (
            // Figma "Footnote" 12/500, Font/Secondary, 8px below the title
            <Typography
              sx={{ fontSize: "12px !important", fontWeight: 500, lineHeight: 1.2, pt: 1, color: FONT_SECONDARY }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>{children}</Box>
      </Box>
      {/* Full-bleed divider omitted on the last section (noPaddingBottom) or when explicitly suppressed (noDivider) */}
      {!noPaddingBottom && !noDivider && <Divider sx={{ mx: -4, borderColor: DIVIDER_COLOR }} />}
    </>
  );
}

export default function EditUserDrawer({ open, userId, onClose, onSaved, mode = "edit" }: EditUserDrawerProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: loadedUser,
    loading: loadingUser,
    error: loadError,
  } = useGetUser({ id: userId ?? "", lazy: !open || !userId });

  const { mutate: updateUser, loading: savingUpdate } = useUpdateUser({ id: userId ?? "" });
  const { mutate: createUser, loading: savingCreate } = useCreateUser({});
  const saving = mode === "add" ? savingCreate : savingUpdate;

  // Add-mode form fields (unused in edit mode)
  const [addFirstName, setAddFirstName] = useState("");
  const [addFirstNameTouched, setAddFirstNameTouched] = useState(false);
  const [addLastName, setAddLastName] = useState("");
  const [addLastNameTouched, setAddLastNameTouched] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addEmailTouched, setAddEmailTouched] = useState(false);
  const [addRoles, setAddRoles] = useState<string[]>([]);
  const [addOpsGroup, setAddOpsGroup] = useState("");
  const [addOktaId, setAddOktaId] = useState("");
  const [addOktaIdTouched, setAddOktaIdTouched] = useState(false);

  // Reset all editable fields when the drawer closes.
  // Add-mode text fields are always cleared; shared chip fields are also cleared so that
  // reopening in either mode starts fresh (edit mode re-initialises them from loadedUser).
  useEffect(() => {
    if (!open) {
      setAddFirstName("");
      setAddFirstNameTouched(false);
      setAddLastName("");
      setAddLastNameTouched(false);
      setAddEmail("");
      setAddEmailTouched(false);
      setAddRoles([]);
      setAddOpsGroup("");
      setAddOktaId("");
      setAddOktaIdTouched(false);
      setSkills([]);
      setQueues([]);
      setLicensedStates([]);
      setFacilityNpis([]);
      setAuthCategories([]);
    }
  }, [open]);

  // Server-backed dropdown options (RQM-430): fetch all skills/queues/states when drawer opens.
  // All are small bounded sets — safe to load in full.
  // Skills, queues and auth categories are scoped to the ops group of the user being edited.
  // In edit mode, wait for the user to load so the first request isn't scoped to the caller.
  const targetOpsGroup = (mode === "add" ? addOpsGroup : loadedUser?.opsGroup)?.trim() || undefined;
  const scopedOptionsLazy = !open || (mode !== "add" && !loadedUser);
  const scopedQueryParams = useMemo(() => ({ opsGroup: targetOpsGroup }), [targetOpsGroup]);

  const { data: allSkillsData, error: skillsError } = useGetSkills({
    lazy: scopedOptionsLazy,
    queryParams: scopedQueryParams,
  });
  const { data: allQueuesData, error: queuesError } = useGetAllQueues({
    lazy: scopedOptionsLazy,
    queryParams: scopedQueryParams,
  });
  const { data: serverStates, error: statesError } = useGetReferenceStates({ lazy: !open });
  const { data: allAuthCategoriesData, error: authCategoriesError } = useGetReferenceAuthCategories({
    lazy: scopedOptionsLazy,
    queryParams: scopedQueryParams,
  });
  const { data: allRolesData, error: rolesError } = useGetReferenceRoles({ lazy: !open });
  const { data: allOpsGroupsData, error: opsGroupsError } = useGetReferenceOpsGroups({ lazy: !open });

  useEffect(() => {
    if (skillsError) {
      logError(
        `Failed to load skill options: ${skillsError instanceof Error ? skillsError.message : String(skillsError)}`
      );
    }
  }, [skillsError]);
  useEffect(() => {
    if (queuesError) {
      logError(
        `Failed to load queue options: ${queuesError instanceof Error ? queuesError.message : String(queuesError)}`
      );
    }
  }, [queuesError]);
  useEffect(() => {
    if (statesError) {
      logError(
        `Failed to load state options: ${statesError instanceof Error ? statesError.message : String(statesError)}`
      );
    }
  }, [statesError]);
  useEffect(() => {
    if (authCategoriesError) {
      logError(
        `Failed to load auth category options: ${
          authCategoriesError instanceof Error ? authCategoriesError.message : String(authCategoriesError)
        }`
      );
    }
  }, [authCategoriesError]);

  useEffect(() => {
    if (rolesError) {
      logError(`Failed to load role options: ${rolesError instanceof Error ? rolesError.message : String(rolesError)}`);
    }
  }, [rolesError]);
  useEffect(() => {
    if (opsGroupsError) {
      logError(
        `Failed to load ops group options: ${
          opsGroupsError instanceof Error ? opsGroupsError.message : String(opsGroupsError)
        }`
      );
    }
  }, [opsGroupsError]);

  // .filter(Boolean) intentionally drops skills/queues with missing or empty names (malformed backend data)
  const skillOptions = useMemo(
    () => (allSkillsData ?? []).map((s) => s.name).filter(Boolean) as string[],
    [allSkillsData]
  );
  const queueOptions = useMemo(
    () => (allQueuesData ?? []).map((q) => q.name).filter(Boolean) as string[],
    [allQueuesData]
  );
  // rolesOptions: displayed label → stored value map. Label shown in UI, value sent to DB.
  const rolesOptions = useMemo(() => (allRolesData ?? []).filter((r) => r.value), [allRolesData]);
  const rolesValueByLabel = useMemo(
    () => Object.fromEntries(rolesOptions.map((r) => [r.label ?? r.value!, r.value!])),
    [rolesOptions]
  );
  const rolesLabelOptions = useMemo(
    () => rolesOptions.map((r) => r.label ?? r.value!).filter(Boolean) as string[],
    [rolesOptions]
  );
  // opsGroupOptions: for opsGroup the API returns ReferenceDataItem[] where label === value
  // (e.g. { label: "Humana", value: "Humana" }). There is no separate enum key to map back to,
  // so we store the label directly — no rolesValueByLabel-style lookup is needed here.
  // This is intentional and confirmed: qm-service persists opsGroup as the human-readable
  // string, not a separate enum value, so label and stored value are always the same.
  const opsGroupOptions = useMemo(
    () => (allOpsGroupsData ?? []).map((r) => r.label ?? r.value ?? "").filter(Boolean) as string[],
    [allOpsGroupsData]
  );

  // Build value↔label maps and picker options in a single pass.
  // Keyed by value so duplicate values are naturally collapsed; Set on the labels guards
  // against two distinct values sharing the same display label (duplicate chip keys / silent
  // save collision).
  const { valueToLabel, labelToValue, authCategoryOptions } = useMemo(() => {
    const vtl: Record<string, string> = {};
    const ltv: Record<string, string> = {};
    (allAuthCategoriesData ?? []).forEach((item) => {
      if (item.value && item.label) {
        vtl[item.value] = item.label;
        ltv[item.label] = item.value;
      }
    });
    return {
      valueToLabel: vtl,
      labelToValue: ltv,
      authCategoryOptions: Array.from(new Set(Object.values(vtl))),
    };
  }, [allAuthCategoriesData]);

  // Persisted fields (round-trip through PATCH /user/{id}).
  const [skills, setSkills] = useState<string[]>([]);
  const [queues, setQueues] = useState<string[]>([]);
  const [licensedStates, setLicensedStates] = useState<string[]>([]);
  const [facilityNpis, setFacilityNpis] = useState<string[]>([]);
  // Active queue (RQM-565): the queue a user lands in on login. Stored as a queue _id.
  const [activeQueueId, setActiveQueueId] = useState<string | undefined>(undefined);
  const [authCategories, setAuthCategories] = useState<string[]>([]);

  // In edit mode, re-initialise chip fields whenever a fresh loadedUser arrives from the API.
  // Each fetch (triggered on drawer open) creates a new object reference via JSON.parse,
  // so this effect fires on every open even when the server returns identical data.
  // Scoped to edit mode: add mode always starts with empty chips regardless of loadedUser.
  useEffect(() => {
    if (loadedUser && mode === "edit") {
      setSkills(loadedUser.skills ?? []);
      setQueues(loadedUser.queues ?? []);
      setLicensedStates(loadedUser.licensedStates ?? []);
      setFacilityNpis(loadedUser.facilityNpis?.map((n) => n.trim()) ?? []);
      setActiveQueueId(loadedUser.activeQueueId);
      setAuthCategories(
        loadedUser.authCategoryPriorities
          ? Object.entries(loadedUser.authCategoryPriorities)
              .sort(([, a], [, b]) => a - b)
              .map(([category]) => category)
          : []
      );
      setAuthCategories(sortedAuthCategoryValues(loadedUser.authCategoryPriorities));
    }
  }, [loadedUser, mode]);

  // Server-backed states with static fallback. RQM-434 returns {value,label}[] objects.
  const stateOptions = useMemo(() => {
    if (!serverStates || serverStates.length === 0) {
      return US_STATE_OPTIONS;
    }
    return serverStates.map((s) => s.value ?? "").filter(Boolean);
  }, [serverStates]);
  const stateOptionsHook = useMemo(() => makeStaticOptionsHook(stateOptions), [stateOptions]);
  const queueOptionsHook = useMemo(() => makeStaticOptionsHook(queueOptions), [queueOptions]);
  const rolesOptionsHook = useMemo(() => makeStaticOptionsHook(rolesLabelOptions), [rolesLabelOptions]);
  const opsGroupOptionsHook = useMemo(() => makeStaticOptionsHook(opsGroupOptions), [opsGroupOptions]);

  // Active-queue options (RQM-565): only the queues currently STAGED in the drawer are
  // selectable, so editing Assigned queues in the same save keeps the active-queue choice
  // consistent (AC-5). Options carry {id, name} — the dropdown shows the name but stores the id.
  const activeQueueOptions = useMemo<ActiveQueueOption[]>(
    () =>
      (allQueuesData ?? []).filter((q) => q.name && queues.includes(q.name)).map((q) => ({ id: q.id, name: q.name })),
    [allQueuesData, queues]
  );
  const activeQueueOptionsHook = useMemo<SelectOptionsHook<ActiveQueueOption>>(
    () => makeStaticObjectOptionsHook(activeQueueOptions, (q) => q.name ?? ""),
    [activeQueueOptions]
  );
  const selectedActiveQueue = useMemo(
    () => activeQueueOptions.find((q) => q.id === activeQueueId) ?? null,
    [activeQueueOptions, activeQueueId]
  );

  // If the active queue is dropped from Assigned queues in the same edit, clear it so we never
  // persist an activeQueueId outside the saved queue list (AC-5; mirrors qm-service auto-repoint).
  // Clear with "" (not undefined): qm-service merges activeQueueId with `!= null` semantics —
  // an omitted/undefined field leaves the existing value, "" clears it. Guarding on allQueuesData
  // being loaded avoids a transient window where the drawer opens before the queue list would
  // have a valid activeQueueId cleared (and the drawer marked dirty).
  useEffect(() => {
    if (allQueuesData && activeQueueId && !activeQueueOptions.some((q) => q.id === activeQueueId)) {
      setActiveQueueId("");
    }
  }, [activeQueueId, activeQueueOptions, allQueuesData]);

  const originalAuthCategories = useMemo(
    () => sortedAuthCategoryValues(loadedUser?.authCategoryPriorities),
    [loadedUser]
  );

  const isDirty = useMemo(() => {
    if (mode === "add") {
      return (
        addFirstName.trim().length > 0 &&
        addLastName.trim().length > 0 &&
        isValidEmail(addEmail) &&
        addOpsGroup.trim().length > 0 &&
        addOktaId.trim().length > 0
      );
    }
    if (!loadedUser) {
      return false;
    }
    return (
      !arraysEqual(skills, loadedUser.skills ?? []) ||
      !arraysEqual(queues, loadedUser.queues ?? []) ||
      !arraysEqual(licensedStates, loadedUser.licensedStates ?? []) ||
      !arraysEqual(facilityNpis, loadedUser.facilityNpis ?? []) ||
      activeQueueId !== loadedUser.activeQueueId ||
      !arraysEqual(authCategories, originalAuthCategories)
    );
  }, [
    mode,
    addFirstName,
    addLastName,
    addEmail,
    addOpsGroup,
    addOktaId,
    loadedUser,
    skills,
    queues,
    licensedStates,
    facilityNpis,
    activeQueueId,
    authCategories,
    originalAuthCategories,
  ]);

  const handleSave = async () => {
    const authCategoryPriorities: { [key: string]: number } = {};
    authCategories.forEach((cat, idx) => {
      authCategoryPriorities[cat] = idx + 1;
    });

    if (mode === "add") {
      const newUser: User = {
        name: [addFirstName.trim(), addLastName.trim()].filter(Boolean).join(" ") || undefined,
        username: addEmail.trim(),
        email: addEmail.trim(),
        oktaId: addOktaId.trim(),
        // Map display labels back to enum values for DB storage. ?? label guards against
        // a label that has no mapping entry (passes it through rather than silently dropping it).
        roles: addRoles.length ? addRoles.map((label) => rolesValueByLabel[label] ?? label) : undefined,
        opsGroup: addOpsGroup.trim() || undefined,
        skills,
        queues,
        licensedStates,
        facilityNpis,
        authCategoryPriorities: Object.keys(authCategoryPriorities).length ? authCategoryPriorities : undefined,
      };
      try {
        const created = await createUser(newUser);
        enqueueSnackbar("User created", { variant: "success" });
        onSaved?.(created as User);
        onClose();
      } catch (err: unknown) {
        const apiErr = err as { status?: number; data?: { message?: string } | string };
        const status = apiErr?.status;
        const serverMessage: string | undefined =
          apiErr?.data && typeof apiErr.data === "object"
            ? apiErr.data.message
            : typeof apiErr?.data === "string"
            ? apiErr.data
            : undefined;
        logError(`Failed to create user (status=${status}): ${serverMessage ?? String(err)}`);
        const userMessage =
          status === 400
            ? "Please check your input details and try again."
            : "Failed to create user. Please try again.";
        enqueueSnackbar(userMessage, { variant: "error" });
      }
      return;
    }

    if (!loadedUser) {
      return;
    }
    // Send only the fields this drawer controls.  Spreading loadedUser would risk
    // overwriting concurrent changes (e.g. roles) made by another admin while the
    // drawer was open — qm-service's whitelist merge applies these fields on top of
    // the server-side record (PR #1521, RQM-428).
    // authCategories state holds enum values (e.g. "MEDICAL_CARE") — authCategoryPriorities
    // is already built from those values at the top of handleSave.
    const patch: User = { skills, queues, licensedStates, facilityNpis, activeQueueId, authCategoryPriorities };
    try {
      await updateUser(patch);
      enqueueSnackbar("User updated", { variant: "success" });
      onSaved?.({ ...loadedUser, ...patch });
      onClose();
    } catch (err) {
      logError(`Failed to update user ${userId}: ${err instanceof Error ? err.message : String(err)}`);
      enqueueSnackbar("Failed to update user. Please try again.", { variant: "error" });
    }
  };

  const roleText = loadedUser?.roles?.length ? loadedUser.roles.join(", ") : "--";
  const opsGroupText = loadedUser?.opsGroup || "--";
  const displayName = loadedUser?.name || "--";

  return (
    <RightDrawer
      open={open}
      onClose={(save) => (save ? handleSave() : onClose())}
      buttonText="Save changes"
      secondaryButtonText="Cancel"
      onSecondaryButtonClick={onClose}
      secondaryButtonDisabled={saving}
      closeDisabled={saving || loadingUser || !isDirty}
      loading={saving}
      dynamicWidth={isSmallScreen ? "100%" : 740}
      compactButtons
      backgroundColor={PAGE_BACKGROUND}
      titleContent={
        <Typography sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.2, color: FONT_PRIMARY }}>
          {mode === "add" ? "Add user" : "Edit user"}
        </Typography>
      }
    >
      <Box data-testid="edit-user-drawer">
        {mode === "edit" && (loadingUser || (!loadError && !loadedUser)) && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }} data-testid="edit-user-loading">
            <CircularProgress />
          </Box>
        )}

        {mode === "edit" && loadError && !loadingUser && (
          <Typography color="error" sx={{ py: 2 }} data-testid="edit-user-load-error">
            Unable to load this user. Please close and try again.
          </Typography>
        )}

        {(mode === "add" || (!loadingUser && !loadError && loadedUser)) && (
          <>
            <Divider sx={{ mx: -4, borderColor: DIVIDER_COLOR }} />
            <Section
              title="Account details"
              subtitle={mode === "edit" ? "Contact admin to edit" : "This info cannot be edited after adding the user"}
              testId={mode === "add" ? "add-user-account-details" : "edit-user-account-details"}
            >
              {mode === "add" ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField
                      variant="outlined"
                      size="small"
                      value={addFirstName}
                      onChange={(e) => setAddFirstName(e.target.value)}
                      onBlur={() => setAddFirstNameTouched(true)}
                      fullWidth
                      error={addFirstNameTouched && addFirstName.trim().length === 0}
                      helperText={
                        addFirstNameTouched && addFirstName.trim().length === 0 ? "First name is required" : undefined
                      }
                      inputProps={{ "aria-label": "First name", placeholder: "First name" }}
                      sx={accountDetailFieldSx}
                    />
                    <TextField
                      variant="outlined"
                      size="small"
                      value={addLastName}
                      onChange={(e) => setAddLastName(e.target.value)}
                      onBlur={() => setAddLastNameTouched(true)}
                      fullWidth
                      error={addLastNameTouched && addLastName.trim().length === 0}
                      helperText={
                        addLastNameTouched && addLastName.trim().length === 0 ? "Last name is required" : undefined
                      }
                      inputProps={{ "aria-label": "Last name", placeholder: "Last name" }}
                      sx={accountDetailFieldSx}
                    />
                  </Box>
                  <TextField
                    variant="outlined"
                    size="small"
                    value={addEmail}
                    onChange={(e) => setAddEmail(e.target.value)}
                    onBlur={() => setAddEmailTouched(true)}
                    fullWidth
                    error={
                      (addEmailTouched && addEmail.trim().length === 0) ||
                      (addEmail.trim().length > 0 && !isValidEmail(addEmail))
                    }
                    helperText={
                      addEmailTouched && addEmail.trim().length === 0
                        ? "Email is required"
                        : addEmail.trim().length > 0 && !isValidEmail(addEmail)
                        ? "Enter a valid email address"
                        : undefined
                    }
                    inputProps={{ "aria-label": "Email", placeholder: "Email" }}
                    sx={accountDetailFieldSx}
                  />
                  <MultipleSelect<string>
                    selectedValue={addRoles}
                    setSelectedValue={(value) => setAddRoles((value as string[]) ?? [])}
                    useOptions={rolesOptionsHook}
                    emptyLabel="Role"
                  />
                  <SingleSelect<string, false>
                    selectedValue={addOpsGroup || null}
                    setSelectedValue={(val) => setAddOpsGroup((val as string | null) ?? "")}
                    useOptions={opsGroupOptionsHook}
                    emptyLabel="Ops group"
                    withDropdownArrow
                  />
                  <TextField
                    variant="outlined"
                    size="small"
                    value={addOktaId}
                    onChange={(e) => setAddOktaId(e.target.value)}
                    onBlur={() => setAddOktaIdTouched(true)}
                    fullWidth
                    error={addOktaIdTouched && addOktaId.trim().length === 0}
                    helperText={addOktaIdTouched && addOktaId.trim().length === 0 ? "Okta ID is required" : undefined}
                    inputProps={{ "aria-label": "Okta ID", placeholder: "Okta ID" }}
                    sx={accountDetailFieldSx}
                  />
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {/* Figma 8619:53555: name 16/600 primary + email 14/400 secondary, 8px apart on one row. */}
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.2, color: FONT_PRIMARY }}>
                      {displayName}
                    </Typography>
                    <Typography sx={{ fontSize: 14, lineHeight: 1.2, color: FONT_SECONDARY }}>
                      {loadedUser?.email}
                    </Typography>
                  </Box>
                  {/* Role / Ops group: 14/400 secondary with bold values. */}
                  <Typography sx={{ fontSize: 14, lineHeight: 1.2, color: FONT_SECONDARY }}>
                    Role: <strong>{roleText}</strong>
                  </Typography>
                  <Typography sx={{ fontSize: 14, lineHeight: 1.2, color: FONT_SECONDARY }}>
                    Ops group: <strong>{opsGroupText}</strong>
                  </Typography>
                </Box>
              )}
            </Section>

            {/* Phase 3 (RQM-437): Status section renders here. Stable slot only for now. */}
            {mode === "edit" && <Box data-testid="edit-user-status-slot" />}

            <Section title="Skillsets" subtitle="Drag fields to reorder prioritization" testId="edit-user-skillsets">
              <EditableChipList
                label="skillset"
                values={skills}
                onChange={setSkills}
                options={skillOptions}
                reorderable
                chipAccentColor={SKILL_CHIP_ACCENT}
                testIdPrefix="edit-user-skillset"
                chipBackGroundColor={SKILL_CHIP_BACKGROUND}
                fontColor={SKILL_CHIP_FONT_COLOR}
              />
            </Section>

            <Section
              title="Assigned queues"
              testId="edit-user-queues"
              alignItems={mode === "add" ? "center" : "flex-start"}
              noDivider
            >
              {mode === "add" ? (
                <MultipleSelect<string>
                  selectedValue={queues}
                  setSelectedValue={(value) => setQueues((value as string[]) ?? [])}
                  useOptions={queueOptionsHook}
                  emptyLabel="Enter queue name"
                />
              ) : (
                <EditableChipList
                  label="queue"
                  values={queues}
                  onChange={setQueues}
                  options={queueOptions}
                  chipAccentColor={QUEUE_CHIP_ACCENT}
                  testIdPrefix="edit-user-queue"
                  chipBackGroundColor={QUEUE_CHIP_BACKGROUND}
                  fontColor={QUEUE_CHIP_FONT_COLOR}
                />
              )}
            </Section>

            <Section
              title="Active queue"
              subtitle="This sets the default queue that a user will see when they first log in"
              testId="edit-user-active-queue"
            >
              <SingleSelect<ActiveQueueOption, false>
                selectedValue={selectedActiveQueue}
                setSelectedValue={(value) => setActiveQueueId((value as ActiveQueueOption | null)?.id ?? undefined)}
                useOptions={activeQueueOptionsHook}
                getOptionLabel={(q) => q.name ?? ""}
                getOptionSelected={(opt, val) => opt.id === val.id}
                emptyLabel="Queue name"
                withDropdownArrow
                clearable
                markSelectedOptions={false}
                data-testid="edit-user-active-queue-input"
              />
            </Section>

            <Section title="State licenses" testId="edit-user-state-licenses" alignItems="center" noPaddingBottom>
              <MultipleSelect<string>
                selectedValue={licensedStates}
                setSelectedValue={(value) => setLicensedStates((value as string[]) ?? [])}
                useOptions={stateOptionsHook}
                emptyLabel="State licenses"
              />
            </Section>

            <Section
              title="Facility NPIs"
              alignItems="center"
              noPaddingBottom
              titleAdornment={
                <Tooltip
                  title="NPIs for the facilities this user is licensed to work with"
                  placement="top"
                  arrow
                  componentsProps={tooltipComponentsProps}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: FONT_SECONDARY, cursor: "default" }} />
                </Tooltip>
              }
              testId="edit-user-facility-npis"
            >
              {/*
               * selectedValue converts the persisted NPI strings to minimal stub objects so the
               * MultipleSelect can render chips immediately without a round-trip.  Chips show
               * the NPI; the dropdown shows "Facility Name / NPI - {npi}" once the API resolves.
               * customGetOptionSelected compares by NPI so stubs match live catalogue entries.
               */}
              <MultipleSelect<ProviderFacilityCatalogue>
                selectedValue={facilityNpis.map(
                  (npi): ProviderFacilityCatalogue => ({ id: npi, npi, dateCreated: "", lastUpdated: "" })
                )}
                setSelectedValue={(vals) =>
                  setFacilityNpis(
                    ((vals as ProviderFacilityCatalogue[]) ?? [])
                      .map((f) => f.npi?.trim())
                      .filter((npi): npi is string => Boolean(npi))
                  )
                }
                useOptions={useFacilityNpiOptions}
                getOptionLabel={facilityNpiLabel}
                customGetOptionSelected={(opt, val) => Boolean(opt.npi) && opt.npi === val.npi}
                emptyLabel={mode === "add" ? "Facility NPIs" : "Search by NPI or facility name"}
                data-testid="edit-user-facility-npis-input"
              />
            </Section>

            <Section
              title="Auth category"
              subtitle="Drag to reorder: highest priority first"
              titleAdornment={
                <Tooltip
                  title="Priority order for authorization categories this user handles"
                  placement="top"
                  arrow
                  componentsProps={tooltipComponentsProps}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: FONT_SECONDARY, cursor: "default" }} />
                </Tooltip>
              }
              testId="edit-user-auth-category"
              noDivider
            >
              <EditableChipList
                label="auth category"
                values={authCategories.map((v) => valueToLabel[v] ?? v)}
                onChange={(labels) => setAuthCategories((labels as string[]).map((l) => labelToValue[l] ?? l))}
                options={authCategoryOptions}
                reorderable
                chipAccentColor={AUTH_CAT_CHIP_ACCENT}
                testIdPrefix="edit-user-auth-category"
                chipBackGroundColor={AUTH_CAT_CHIP_BACKGROUND}
                fontColor={AUTH_CAT_CHIP_FONT_COLOR}
              />
            </Section>

            {/* Phase 3 (RQM-437): "Deactivate user" link renders here. Stable slot only for now. */}
            {mode === "edit" && <Box sx={{ py: 2 }} data-testid="edit-user-deactivate-slot" />}
          </>
        )}
      </Box>
    </RightDrawer>
  );
}
